// Org / Utilization tab — orchestrator.
//
// Owns:
//   - Date range state (defaults to last 7 days)
//   - View mode state ('chart' | 'dashboard')
//   - Data fetch (/api/org-utilization)
//
// Delegates rendering to OrgChart (d3-org-chart) and OrgDashboard
// (Chart.js + nested accordion). Both components are pure presenters
// that take the same `data` payload from the API.
//
// Server enforces visibility via the viewer uid (passed up from
// AppShell). This component never receives or filters data outside the
// viewer's scope; it just renders what the server sends back.

import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import OrgChart from './OrgChart.jsx';
import OrgDashboard from './OrgDashboard.jsx';

function isoDate(d) { return d.toISOString().slice(0, 10); }
function lastNDays(n) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - n);
  return { startDate: isoDate(start), endDate: isoDate(end) };
}

export default function OrgTab({ viewerUid, viewerName, orgMe }) {
  const initial = lastNDays(7);
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate]     = useState(initial.endDate);
  // Pending values: edited in the inputs but not applied until "Apply".
  // Avoids re-fetching on every keystroke.
  const [pendingStart, setPendingStart] = useState(initial.startDate);
  const [pendingEnd,   setPendingEnd]   = useState(initial.endDate);
  const [view, setView] = useState('chart'); // 'chart' | 'dashboard'

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!viewerUid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.orgUtilization(viewerUid, { startDate, endDate })
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message || 'Failed to load org data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewerUid, startDate, endDate]);

  const applyDates = () => {
    setStartDate(pendingStart);
    setEndDate(pendingEnd);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Team utilization</h2>
          <p className="text-xs text-muted mt-0.5">
            {orgMe?.hasFullScope
              ? `Full company view — ${data?.nodes?.length ?? '…'} people`
              : `Your team — ${data?.nodes?.length ?? '…'} people${viewerName ? ' under ' + viewerName.split(' ')[0] : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center rounded-md border border-border bg-panel p-0.5" role="group">
            <button
              onClick={() => setView('chart')}
              className={`px-3 py-1 text-xs rounded ${view === 'chart' ? 'bg-accent text-white font-medium' : 'text-muted hover:text-text'}`}
            >Org Chart</button>
            <button
              onClick={() => setView('dashboard')}
              className={`px-3 py-1 text-xs rounded ${view === 'dashboard' ? 'bg-accent text-white font-medium' : 'text-muted hover:text-text'}`}
            >Dashboard</button>
          </div>
          <label className="text-[11px] text-muted inline-flex items-center gap-1">
            From
            <input
              type="date"
              value={pendingStart}
              onChange={e => setPendingStart(e.target.value)}
              className="px-2 py-1 rounded border border-border bg-panel text-xs text-text focus:border-accent focus:outline-none"
            />
          </label>
          <label className="text-[11px] text-muted inline-flex items-center gap-1">
            To
            <input
              type="date"
              value={pendingEnd}
              onChange={e => setPendingEnd(e.target.value)}
              className="px-2 py-1 rounded border border-border bg-panel text-xs text-text focus:border-accent focus:outline-none"
            />
          </label>
          <button
            onClick={applyDates}
            disabled={loading || (pendingStart === startDate && pendingEnd === endDate)}
            className="px-3 py-1 rounded-md bg-accent hover:bg-accent2 disabled:opacity-50 disabled:hover:bg-accent text-white text-xs font-semibold transition-colors"
          >
            {loading ? 'Loading…' : 'Apply'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg border border-danger/30 bg-danger/10 text-sm text-danger">
          {error}
        </div>
      )}

      {!data && !error && (
        <div className="p-8 rounded-lg border border-border bg-panel text-center text-muted text-sm">
          {loading ? 'Loading team data…' : 'No data yet.'}
        </div>
      )}

      {data && view === 'chart' && (
        <OrgChart data={data} />
      )}
      {data && view === 'dashboard' && (
        <OrgDashboard data={data} />
      )}
    </div>
  );
}
